import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, FilePdf, Receipt } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const MINV_SERVICES = [
  "Accident Recovery Car Towing",
  "Breakdown Recovery Car Towing",
  "Accident Recovery Motorcycle Towing",
  "Breakdown Recovery Motorcycle Towing",
  "Battery Rescue (Jumper + Inspection)",
  "Battery Rescue (Battery Exchange 1 to 1)",
  "Fuel Rescue",
];

const emptyItem = () => ({ id: crypto.randomUUID(), description: "", qty: 1, unit_price: "" });

function ManualInvoiceForm({ onDone }) {
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("");
  const [company, setCompany] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState("");

  const loadCompanies = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { loadCompanies(); }, []);

  const addCompany = async () => {
    if (!newCompany.trim()) return;
    try {
      await api.post("/companies", { name: newCompany.trim() });
      toast.success("Company added");
      setNewCompany("");
      loadCompanies();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const total = items.reduce((a, i) => a + (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);

  const setItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const submit = async (e) => {
    e.preventDefault();
    if (!category) {
      toast.error("Category is required");
      return;
    }
    if (category === "inhouse" && !company) {
      toast.error("Select a company for inhouse invoices");
      return;
    }
    if (category === "outsource" && !serviceType) {
      toast.error("Select a service for outsource invoices");
      return;
    }
    if (!customerName.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/manual", {
        customer_name: customerName, customer_address: customerAddress,
        date, due_date: dueDate, items, notes,
        category, company: category === "inhouse" ? company : "",
        service_type: category === "outsource" ? serviceType : "",
      });
      toast.success(`Invoice ${data.invoice_no} generated`);
      await downloadFile(`/invoices/${data.id}/file`, `${data.invoice_no}.pdf`);
      setCustomerName(""); setCustomerAddress(""); setItems([emptyItem()]); setNotes(""); setDueDate("");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="manual-invoice-form">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Customer Name *</Label>
            <Input data-testid="minv-customer-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Customer Address</Label>
            <Input data-testid="minv-customer-address" value={customerAddress} onChange={(e) => setCustomerAddress(e.target.value)}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</Label>
            <Input data-testid="minv-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Due Date</Label>
            <Input data-testid="minv-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="minv-category-section">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Category *</Label>
            <Select value={category} onValueChange={(v) => { setCategory(v); setCompany(""); setServiceType(""); }}>
              <SelectTrigger data-testid="minv-category" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="inhouse">Inhouse</SelectItem>
                <SelectItem value="outsource">Outsource</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {category === "inhouse" && (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Company *</Label>
              <Select value={company} onValueChange={setCompany}>
                <SelectTrigger data-testid="minv-company" className="rounded-sm border-input bg-background focus:ring-primary">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {companies.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input data-testid="minv-add-company-input" value={newCompany} onChange={(e) => setNewCompany(e.target.value)}
                  placeholder="Add new company..." className="rounded-sm border-input bg-background focus-visible:ring-primary" />
                <Button type="button" variant="outline" data-testid="minv-add-company-button" onClick={addCompany}
                  className="shrink-0 rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">Add</Button>
              </div>
            </div>
          )}
          {category === "outsource" && (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Service *</Label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger data-testid="minv-service" className="rounded-sm border-input bg-background focus:ring-primary">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent className="bg-popover">
                  {MINV_SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Line Items</Label>
          {items.map((it, i) => (
            <div key={it.id} className="grid grid-cols-[1fr_80px_120px_100px_40px] items-center gap-2" data-testid={`minv-item-${i}`}>
              <Input data-testid={`minv-item-desc-${i}`} placeholder="Description" value={it.description}
                onChange={(e) => setItem(i, { description: e.target.value })}
                className="rounded-sm border-input bg-background focus-visible:ring-primary" />
              <Input data-testid={`minv-item-qty-${i}`} type="number" min="0" step="1" placeholder="Qty" value={it.qty}
                onChange={(e) => setItem(i, { qty: e.target.value })}
                className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
              <Input data-testid={`minv-item-price-${i}`} type="number" min="0" step="0.01" placeholder="Unit RM" value={it.unit_price}
                onChange={(e) => setItem(i, { unit_price: e.target.value })}
                className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
              <p className="text-right font-mono text-sm font-bold" data-testid={`minv-item-total-${i}`}>
                {((parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0)).toFixed(2)}
              </p>
              <button type="button" data-testid={`minv-item-remove-${i}`} onClick={() => setItems(items.filter((_, x) => x !== i))}
                className="rounded-sm p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-red-400">
                <Trash size={16} />
              </button>
            </div>
          ))}
          <Button type="button" variant="outline" data-testid="minv-add-item"
            onClick={() => setItems([...items, emptyItem()])}
            className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
            <Plus size={14} className="mr-1" /> Add Line Item
          </Button>
        </div>

        <div className="flex items-center justify-between rounded-sm border border-primary/40 bg-primary/5 px-6 py-4">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Invoice Total</span>
          <span data-testid="minv-total" className="font-mono text-2xl font-black text-primary">RM {total.toFixed(2)}</span>
        </div>

        <Textarea data-testid="minv-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (optional)" className="rounded-sm border-input bg-background focus-visible:ring-primary" />

        <Button data-testid="minv-submit-button" type="submit" disabled={busy}
          className="w-full rounded-sm bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
          {busy ? "Generating..." : "Generate PDF Invoice"}
        </Button>
    </form>
  );
}

export default function InvoicesPanel() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const canDelete = ["ceo", "coceo", "dev"].includes(user?.role);

  const refresh = useCallback(() => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Invoice ${inv.invoice_no} deleted`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Tabs defaultValue="all" data-testid="invoices-panel">
      <TabsList className="mb-6 rounded-sm border border-border bg-card">
        <TabsTrigger data-testid="tab-all-invoices" value="all" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">All Invoices</TabsTrigger>
        <TabsTrigger data-testid="tab-manual-invoice" value="manual" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">New Manual Invoice</TabsTrigger>
      </TabsList>

      <TabsContent value="all">
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="invoices-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Invoice No</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Source</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Customer</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Job</TableHead>
                <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Total</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} className="border-border hover:bg-muted/50" data-testid={`invoice-row-${inv.invoice_no}`}>
                  <TableCell className="font-mono text-sm font-bold text-primary">{inv.invoice_no}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-sm font-bold uppercase tracking-wider ${inv.source === "manual" ? "border-sky-500/40 text-sky-400" : "border-emerald-500/40 text-emerald-400"}`}>
                      {inv.source === "manual" ? "Manual" : "Auto"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{inv.customer_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{inv.job_no || "—"}</TableCell>
                  <TableCell className="text-right font-mono font-bold">RM {Number(inv.total || 0).toFixed(2)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{inv.date}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" data-testid={`invoice-download-${inv.invoice_no}`}
                        onClick={() => downloadFile(`/invoices/${inv.id}/file`, `${inv.invoice_no}.pdf`)}
                        className="rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                        <FilePdf size={14} className="mr-1" /> PDF
                      </Button>
                      {canDelete && (
                        <Button size="sm" variant="outline" data-testid={`invoice-delete-${inv.invoice_no}`}
                          onClick={() => deleteInvoice(inv)}
                          className="rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!invoices.length && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  <Receipt size={28} className="mx-auto mb-3 text-muted-foreground" />No invoices yet.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>

      <TabsContent value="manual">
        <ManualInvoiceForm onDone={refresh} />
      </TabsContent>
    </Tabs>
  );
}
